import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clapperboard, Package } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ArdiOrb } from '../../../../components/design-system/common/ArdiOrb';
import { MediaChip } from '../../../../components/design-system/common/MediaChip';

import { StudioBoard, StudioChatMessage } from '../studio-contract';
import { ProductInfo } from './ShotCard';
import { LightboxMedia, MediaLightbox } from '../../../../components/design-system/common/MediaLightbox';
import { Composer, ComposerChip } from './Composer';

interface ArdiPanelProps {
    messages: StudioChatMessage[];
    busy: boolean;
    activity: string | null;
    draft: string;
    onDraft: (v: string) => void;
    onSend: (text?: string) => void;
    onStop: () => void;
    chips: ComposerChip[];
    onRemoveChip: (key: string) => void;
    onAttachReference: (file: File) => void;
    onPickProducts: () => void;
    onMentionProduct?: (product: ProductInfo) => void;
    /** The production's reference — what a "reference" attachment on a message opens. */
    reference?: StudioBoard['reference'];
    /** Products known on this page, to show a product attachment's picture. */
    products: Map<string, ProductInfo>;
    starters: string[];
}

/**
 * The conversation with Ardi, beside the board.
 *
 * Ardi's answers are short by design — the board is where the work shows up —
 * so each one ends with buttons for the likely next step rather than a
 * question the vendor has to type an answer to.
 */
export const ArdiPanel: React.FC<ArdiPanelProps> = ({
    messages, busy, activity, draft, onDraft, onSend, onStop, chips, onRemoveChip, onAttachReference, onPickProducts, onMentionProduct, reference, products, starters,
}) => {
    const { t } = useTranslation();
    const [previewing, setPreviewing] = useState<LightboxMedia | null>(null);
    const endRef = useRef<HTMLDivElement>(null);
    useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, busy]);
    const last = messages[messages.length - 1];

    /** A product attachment's picture: saved with the message, or found by uuid or name among the page's products. */
    const productOf = (a: NonNullable<StudioChatMessage['attachments']>[number]): ProductInfo | undefined => {
        if (a.productUuid && products.get(a.productUuid)) return products.get(a.productUuid);
        const name = a.label.trim().toLowerCase();
        return Array.from(products.values()).find((p) => p.name.trim().toLowerCase() === name);
    };

    const attachment = (a: NonNullable<StudioChatMessage['attachments']>[number], i: number) => {
        if (a.kind === 'product') {
            const p = productOf(a);
            const thumb = a.url || p?.thumbnailUrl;
            const full = p?.imageUrl || thumb;
            return (
                <MediaChip key={i} size="sm" label={a.label} thumbnailUrl={thumb} icon={<Package />}
                    onOpen={full ? () => setPreviewing({ url: full, kind: 'image', title: a.label }) : undefined}
                    openLabel={t('STUDIO.OPEN_PREVIEW', 'Preview')} />
            );
        }
        const url = a.url || reference?.url;
        const kind = url && reference?.url === url ? reference.kind : (url && /\.(mp4|mov|webm)(\?|$)/i.test(url) ? 'video' : 'image');
        const poster = reference?.frames?.[0]?.url;
        return (
            <MediaChip key={i} size="sm" label={a.label}
                thumbnailUrl={kind === 'image' ? url : poster}
                videoUrl={kind === 'video' && !poster ? url : undefined}
                icon={<Clapperboard />}
                onOpen={url ? () => setPreviewing({ url, kind: kind === 'video' ? 'video' : 'image', title: a.label }) : undefined}
                openLabel={t('STUDIO.OPEN_PREVIEW', 'Preview')} />
        );
    };

    /** Product attachments named in the message's own words: shown there, as chips, instead of above it. */
    const namedIn = (m: StudioChatMessage) => (m.attachments || []).filter((a) => a.kind === 'product' && a.label.trim()
        && m.text.toLowerCase().includes(a.label.trim().toLowerCase()));

    const userText = (m: StudioChatMessage) => {
        const named = namedIn(m);
        if (!named.length) return m.text;
        // Longest first, so "Oak Table" wins over "Oak" when both are attached.
        const labels = named.map((a) => a.label.trim()).sort((a, b) => b.length - a.length);
        const pattern = new RegExp(`(${labels.map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
        return m.text.split(pattern).map((part, i) => {
            const a = named.find((x) => x.label.trim().toLowerCase() === part.toLowerCase());
            if (!a) return part;
            const chip = attachment(a, i);
            return <span key={i} className="mx-0.5 inline-flex align-middle [&>span]:bg-background">{chip}</span>;
        });
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5">
                {messages.length === 0 && (
                    <div className="space-y-4 pt-2">
                        <div className="flex items-center gap-2.5">
                            <ArdiOrb size={28} />
                            <div className="text-sm font-medium text-foreground">{t('STUDIO.ARDI.HELLO', 'What are we making?')}</div>
                        </div>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                            {t('STUDIO.ARDI.HELLO_HINT', 'Tell me what you need, or drop in a video you love — I will plan the shots and make the photos. You approve, then we film.')}
                        </p>
                        <div className="flex flex-col gap-1.5">
                            {starters.map((s) => (
                                <button key={s} type="button" onClick={() => onSend(s)} disabled={busy}
                                    className="cursor-pointer rounded-xl border px-3 py-2 text-start text-sm text-foreground transition-colors hover:bg-accent">
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m) => (
                    <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                        {m.role === 'user' ? (
                            <div className="flex max-w-[88%] flex-col items-end gap-1.5">
                                {(() => {
                                    const named = namedIn(m);
                                    const above = (m.attachments || []).filter((a) => !named.includes(a));
                                    return above.length ? (
                                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                                            {above.map(attachment)}
                                        </div>
                                    ) : null;
                                })()}
                                <div className="whitespace-pre-wrap rounded-2xl rounded-ee-md bg-muted px-3.5 py-2 text-sm leading-relaxed text-foreground">{userText(m)}</div>
                            </div>
                        ) : (
                            <div className="flex max-w-[94%] gap-2.5">
                                <ArdiOrb size={22} className="mt-0.5 shrink-0" />
                                <div className="min-w-0 space-y-2.5">
                                    {m.text && <div className={cn('whitespace-pre-wrap text-sm leading-relaxed', m.error ? 'text-muted-foreground' : 'text-foreground')}>{m.text}</div>}
                                    {m.question && (
                                        <div className="space-y-1.5">
                                            <div className="text-sm font-medium text-foreground">{m.question.question}</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {m.question.options.map((o) => (
                                                    <button key={o.label} type="button" disabled={busy || m !== last} onClick={() => onSend(o.label)}
                                                        className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-start text-xs text-foreground transition-colors hover:bg-accent disabled:cursor-default disabled:opacity-60">
                                                        {o.thumbnailUrl && <img src={o.thumbnailUrl} alt="" className="size-7 rounded-md object-cover" />}
                                                        <span>{o.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {m.suggestions?.length && m === last && !busy ? (
                                        <div className="flex flex-wrap gap-1.5">
                                            {m.suggestions.map((s) => (
                                                <button key={s} type="button" onClick={() => onSend(s)}
                                                    className="cursor-pointer rounded-full border px-3 py-1 text-xs text-foreground transition-colors hover:bg-accent">
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {busy && (
                    <div className="flex items-center gap-2.5 animate-in fade-in">
                        <ArdiOrb size={22} state="thinking" />
                        <span key={activity || ''} className="text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom-1">{activity || t('STUDIO.ARDI.ACT_THINKING', 'Thinking…')}</span>
                    </div>
                )}
                <div ref={endRef} />
            </div>
            <div className="shrink-0 border-t p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
                <Composer
                    value={draft}
                    onChange={onDraft}
                    onSubmit={() => onSend()}
                    busy={busy}
                    onStop={onStop}
                    chips={chips}
                    onRemoveChip={onRemoveChip}
                    onAttachReference={onAttachReference}
                    onPickProducts={onPickProducts}
                    onMentionProduct={onMentionProduct}
                    placeholder={t('STUDIO.ARDI.PLACEHOLDER', 'Ask Ardi — "make it more cinematic", "add a close-up of the fringe"…')}
                />
                <p className="mt-1.5 px-1 text-[11px] text-muted-foreground">{t('STUDIO.ARDI.COST_NOTE', 'Chatting with Ardi costs next to nothing. You only pay for photos and films you make.')}</p>
            </div>
            <MediaLightbox media={previewing} onClose={() => setPreviewing(null)} />
        </div>
    );
};
